import type {
  ProjectActor,
  ProjectVersion,
  ProjectVersionEntityType,
  ProjectVersionFieldChange,
  ProjectVersionOperation,
  ProjectVersionSource,
} from "../types/projectVersion.types";

// Maps rows from the projectVersionNumbered view (Hasura) onto the UI's ProjectVersion type.
// View columns are all nullable in the generated types (Postgres views lose NOT NULL), and
// `changes` is free-form jsonb — so every field is guarded here rather than trusted.

const ENTITY_TYPES: ProjectVersionEntityType[] = [
  "project",
  "material",
  "model",
  "transport",
  "waste",
  "emissionFactor",
  "specialCondition",
  "standardValue",
  "membership",
];
const OPERATIONS: ProjectVersionOperation[] = ["create", "update", "delete"];

/** DB check-constraint values are snake_case (emission_factor); the UI union is camelCase. */
const SNAKE_TO_CAMEL_ENTITY_TYPE: Record<string, ProjectVersionEntityType> = {
  emission_factor: "emissionFactor",
  special_condition: "specialCondition",
  standard_value: "standardValue",
};

const normalizeEntityType = (value: unknown): ProjectVersionEntityType => {
  const camel = typeof value === "string" ? SNAKE_TO_CAMEL_ENTITY_TYPE[value] ?? value : value;
  return ENTITY_TYPES.includes(camel as ProjectVersionEntityType) ? (camel as ProjectVersionEntityType) : "project";
};

const normalizeOperation = (value: unknown): ProjectVersionOperation =>
  OPERATIONS.includes(value as ProjectVersionOperation) ? (value as ProjectVersionOperation) : "update";

const normalizeSource = (value: unknown): ProjectVersionSource => (value === "api" ? "api" : "client");

const asOptionalText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
};

/** Accepts the jsonb changes column in any state and returns only well-formed entries. */
export const parseVersionChanges = (raw: unknown): ProjectVersionFieldChange[] => {
  if (!Array.isArray(raw)) return [];
  const changes: ProjectVersionFieldChange[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const candidate = entry as Record<string, unknown>;
    if (typeof candidate.field !== "string" || candidate.field.length === 0) continue;
    changes.push({
      field: candidate.field,
      from: asOptionalText(candidate.from),
      to: asOptionalText(candidate.to),
      unit: typeof candidate.unit === "string" && candidate.unit.length > 0 ? candidate.unit : undefined,
    });
  }
  return changes;
};

export interface ProjectVersionRowLike {
  id?: string | null;
  versionNumber?: number | null;
  entityType?: string | null;
  entityId?: string | null;
  entityLabel?: string | null;
  operation?: string | null;
  summary?: string | null;
  changes?: unknown;
  source?: string | null;
  informational?: boolean | null;
  martenVersion?: number | null;
  createdBy?: string | null;
  createdAt?: string | null;
  revisionId?: string | null;
}

export const mapVersionRow = (
  row: ProjectVersionRowLike,
  projectId: string,
  resolveActor: (userId: string | null | undefined) => ProjectActor
): ProjectVersion | null => {
  if (!row.id || !row.createdAt) return null;
  return {
    id: row.id,
    projectId,
    versionNumber: row.versionNumber ?? 0,
    revisionId: row.revisionId ?? null,
    entityType: normalizeEntityType(row.entityType),
    entityId: row.entityId ?? null,
    entityLabel: row.entityLabel ?? null,
    operation: normalizeOperation(row.operation),
    summary: row.summary ?? "Change",
    changes: parseVersionChanges(row.changes),
    source: normalizeSource(row.source),
    // Strictly true, so a row from an environment whose view predates the column (it selects as
    // absent, not false) reads as a version — which is exactly what every pre-column row is.
    informational: row.informational === true,
    martenVersion: row.martenVersion ?? null,
    createdBy: resolveActor(row.createdBy),
    createdAt: row.createdAt,
  };
};

/** Milliseconds, treating an unparseable stamp as the epoch so the comparator stays total. */
const timeOf = (version: Pick<ProjectVersion, "createdAt">): number => {
  const parsed = Date.parse(version.createdAt);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** The stored branch-local number, treating a non-finite value as 0 so the comparator stays total. */
const numberOf = (version: Pick<ProjectVersion, "versionNumber">): number =>
  Number.isFinite(version.versionNumber) ? version.versionNumber : 0;

/**
 * Newest first — the order the timeline renders in.
 *
 * `versionNumber` descending leads, because the number is what the page shows and a reader counting
 * down the list has to see it descend. Time cannot carry that on its own any more: since the Hasura
 * migration `1787110000000_project_version_stored_numbers` the number is STORED at insert — the
 * `BEFORE INSERT` trigger `project_version_stamp_revision()` assigns branch head + 1 under a
 * per-branch advisory lock, i.e. in *insert* order — while `created_at` defaults to `now()`, which is
 * *transaction start*. Two overlapping transactions can take their numbers in the opposite order to
 * their stamps, and sorting on time alone rendered the disagreement (RTLCA-5537: a freshly created
 * project read v2, v3, v1 top to bottom).
 *
 * `createdAt` descending stays as the tiebreak, which is what keeps every row the number cannot
 * separate where it belongs:
 *  - informational rows — team changes (RTLCA-5262), exported reports — take no number of their own
 *    and carry the number of the version before them, so time lands them above that version and
 *    below the next one;
 *  - an environment whose view has no `version_number` maps every row to 0 (see mapVersionRow), so
 *    the whole list ties on number and falls straight through to time ordering.
 *
 * Numbers are branch-local, so sibling branches each own a v3 (RTLCA-5268) — but the page sorts
 * AFTER `versionsScopedToRevision`, whose caps admit at most one version row per number, so a
 * sibling's v3 never reaches the sort.
 *
 * Total and deterministic: the number, then whole milliseconds, then the raw stamp (Postgres keeps
 * microseconds, Date.parse truncates them — the strings come from one response, so they share a
 * format and sort lexicographically in temporal order), then the id, the final tiebreaker.
 */
export const compareVersionsNewestFirst = (a: ProjectVersion, b: ProjectVersion): number => {
  const byNumber = numberOf(b) - numberOf(a);
  if (byNumber !== 0) return byNumber;
  const byTime = timeOf(b) - timeOf(a);
  if (byTime !== 0) return byTime;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;
};
